<script lang="ts">
  /**
   * One message in a conversation.
   *
   * The smallest thing both surfaces got wrong in the same way: the extension
   * rendered `{m.content}` and the web app `<p class="whitespace-pre-wrap">{m.content}</p>`,
   * so a DM containing a link showed literally as `[https://x](https://x)` and a
   * mention as `[@Ana](obj://user/…)`. The parser that renders those properly already
   * existed and was already shared — it just was never reached for here.
   *
   * COMPOSED, NOT WRITTEN. `<SkoolContent>` is the token-tree renderer (real
   * elements, no `innerHTML`, so someone else's message cannot inject markup) and
   * `<SkoolMedia>` already draws image grids and Mux video posters. Image attachments
   * are `ImageMedia` in all but name, so they go through that same component rather
   * than a second `<img>` block that would drift from it.
   *
   * WHITESPACE. The bubble does NOT set `whitespace-pre-wrap`: `<SkoolContent>` models
   * newlines itself — a single `\n` is a soft break, a blank line starts a paragraph —
   * and leaving the CSS on doubles every gap.
   */
  import Avatar from '../Avatar.svelte';
  import SkoolContent from '../SkoolContent.svelte';
  import SkoolMedia from '../SkoolMedia.svelte';
  import { fullDate, type ImageMedia, type VideoMedia } from '../member';
  import ReadReceipt from './ReadReceipt.svelte';
  import { timeAgo, type DmMessageView, type Party } from './types';

  let {
    message,
    owner = null,
    counterpart = null,
    skoolUrl = null,
    timestamp = 'relative',
    compact = false,
    now = Date.now(),
    receipt = null,
    body,
  }: {
    message: DmMessageView;
    /**
     * Density, the same way `<SkoolPost>` and `<SkoolComment>` mean it: false is the
     * roomy default a full-width page wants, true is the 340px side panel.
     *
     * This is not decoration. Sharing the component is not the same as sharing a
     * SIZE — 12px text and an 85%-of-the-container bubble read correctly in a narrow
     * panel and read as a mistake stretched across a 700px desktop pane, where the
     * same ratio hands one message most of the width of the screen. So the roomy side
     * also CAPS the bubble in absolute terms, because a percentage alone has no
     * opinion about line length.
     */
    compact?: boolean;
    /**
     * The two ends of the conversation, so each message wears the face of whoever
     * said it.
     *
     * Colour and side alone were not enough. In the superadmin explorer NEITHER party
     * is the reader, so "left is them, right is you" says nothing at all; and even in
     * the panel, a thread skimmed at 340px is two columns of tinted rectangles. Both
     * sides get an avatar rather than just the counterpart, because a transcript read
     * by a third party has no "you" to leave implicit.
     *
     * Either may be null, which simply draws no avatar on that side.
     */
    owner?: Party | null;
    counterpart?: Party | null;
    /** Where a video tile links out to. See `ConversationView.skoolUrl`. */
    skoolUrl?: string | null;
    /**
     * `relative` for a conversation — "3 days ago" is what someone reading one wants,
     * with the instant on hover for when it turns into a support question.
     *
     * `exact` for the superadmin explorer, where the question is "what did we mirror
     * and when", and hover-to-reveal is the wrong affordance for the one column you
     * are scanning.
     *
     * `none` for a live exchange the reader is having right now — an assistant chat,
     * where every message is "just now" and the line says nothing while costing a row
     * under every bubble. It takes the read receipt with it, which is correct: a
     * surface with no time has no wait to report.
     */
    timestamp?: 'relative' | 'exact' | 'none';
    now?: number;
    /**
     * Have they caught up with this one? Drawn as ticks beside the time, and ignored
     * on anything but the owner's own messages — "read" against a message somebody
     * sent US is meaningless.
     *
     * The CALLER decides which messages get it, because the answer Skool gives is per
     * conversation rather than per message. See `<ConversationThread>`, which hands it
     * only to the run of messages sent since the counterpart last spoke.
     */
    receipt?: 'sent' | 'read' | null;
    /**
     * Draw this instead of the message text.
     *
     * WHY IT EXISTS: everything else here — the sides, the two avatars, the tinted
     * bubble, the tail on the corner nearest its author — is what makes a list of
     * messages read as a conversation, and none of it is specific to a DM. The one
     * part that is, is the BODY: `<SkoolContent>` renders Skool's token tree, which
     * is the right renderer for a DM and the wrong one for markdown with citations.
     *
     * So the chrome is shared and the body is the seam. Without it the assistant
     * chat had to reimplement bubbles, alignment and avatars to show one different
     * kind of text — a second conversation layout that would drift from this one.
     *
     * `message.content` is ignored when this is supplied, and an empty message with a
     * body is not "empty".
     */
    body?: import('svelte').Snippet;
  } = $props();

  const mine = $derived(message.outbound === true);
  /** Neither side. See `DmMessageView.outbound` — a fault worth showing, not hiding. */
  const unplaceable = $derived(message.outbound == null);
  /** Whose face goes beside this bubble. Nobody's, when we cannot place the message —
   *  guessing a party there would bury the very thing worth seeing. */
  const said = $derived<Party | null>(unplaceable ? null : mine ? owner : counterpart);

  /** Image attachments ARE `ImageMedia`; the only difference is the field names, so
   *  they are renamed rather than re-rendered. The `md` variant when Skool made one,
   *  the `sm` one otherwise — see `DmAttachment`. */
  const images = $derived<ImageMedia[]>(
    (message.attachments ?? [])
      .filter((a) => a.url || a.thumb_url)
      .map((a) =>
        a.url
          ? { url: a.url, width: a.width, height: a.height }
          : { url: a.thumb_url!, width: a.thumb_width, height: a.thumb_height },
      ),
  );

  /**
   * Anything that is not an image gets a chip naming it, because a preview of a PDF
   * is a broken image and a broken image reads as a bug.
   *
   * NOT A LINK. The mirror keeps the two image sizes Skool publishes and no download
   * url — a non-image has nowhere to point — and an `<a href="#">` that does nothing
   * is a worse answer than plain text. It says what came through; Skool is where the
   * file is. No such attachment has been seen in production yet.
   */
  const files = $derived((message.attachments ?? []).filter((a) => !a.url && !a.thumb_url));

  /**
   * A DM's Mux descriptor as the media component's own shape, so a video in a DM gets
   * the poster tile, the duration, the lightbox and the link out that a video in a
   * post has always had — one renderer, not two (#161).
   *
   * `kind: 'skool'` because the stream is referer-locked to skool.com and cannot play
   * here; the lightbox therefore offers the poster and a way over to Skool.
   *
   * NO TOKEN, NO POSTER. Mux thumbnails are signed, so a row mirrored before the
   * mirror kept `thumbnail_token` yields `poster_url: null` and `<SkoolMedia>` draws
   * its placeholder tile — which is the honest answer, and better than requesting an
   * image that answers 403.
   */
  const videos = $derived<VideoMedia[]>(
    (message.videos ?? []).map((v) => ({
      kind: 'skool',
      poster_url:
        v.playback_id && v.thumbnail_token
          ? `https://image.mux.com/${v.playback_id}/thumbnail.jpg?token=${v.thumbnail_token}`
          : null,
      video_id: v.playback_id,
      provider: null,
      url: null,
      title: null,
      duration_ms: v.duration,
      aspect: v.aspect_ratio ?? null,
    })),
  );

  /** Neither text nor media is the one case worth naming: it means we stored
   *  something we cannot draw. An image-only message is ordinary and common. */
  const empty = $derived(
    !body && !message.content && !images.length && !files.length && !videos.length,
  );
</script>

<!-- The avatar sits OUTSIDE the bubble, on the same side, so the bubble keeps its
     full width for text and a column of faces runs down the edge of the thread.
     `items-end` puts it level with the last line, which is where a reader's eye
     already is when they reach the timestamp. An unplaceable message is centred —
     it belongs to neither column, and pretending otherwise is the bug. -->
<div
  class="flex items-end gap-1.5 {unplaceable
    ? 'justify-center'
    : mine
      ? 'justify-end'
      : 'justify-start'}"
>
  {#if said && !mine}
    <Avatar
      src={said.pictureUrl}
      name={said.name ?? ''}
      size={compact ? 20 : 26}
      memberId={said.memberId ?? null}
    />
  {/if}

  <div
    class="rounded-2xl leading-relaxed break-words
           {compact ? 'max-w-[85%] px-3 py-1.5 text-xs' : 'max-w-[min(85%,46rem)] px-3.5 py-2 text-sm'}
           {unplaceable
             ? 'bg-warning/15 text-base-content ring-1 ring-warning/40'
             : mine
               ? 'rounded-br-sm bg-primary text-primary-content'
               : 'rounded-bl-sm bg-base-200 text-base-content'}"
  >
    {#if unplaceable}
      <!-- The mirror holds this message and cannot say who sent it. Naming that is
           the whole job of the surface that renders it this way. -->
      <div class="mb-0.5 font-mono text-[10px] uppercase tracking-wide text-warning">
        sender unresolved
      </div>
    {/if}

    {#if body}
      {@render body()}
    {:else if message.content}
      <SkoolContent text={message.content} inverted={mine} />
    {/if}

    <!-- Videos first, then images — `SkoolMedia`'s own order, and the order the
         lightbox pages through.

         NOT `clamped`, which is the summary density feed cards use. Clamping also
         suppresses the duration badge, and a DM video usually has no poster to put it
         on: of the nine in the mirror today, none carries a `thumbnail_token`, because
         only messages mirrored after #161 can. A clamped tile would therefore be a
         grey square with a play button — LESS than the "Video · 1:22" chip this
         replaces. Unclamped it keeps the runtime, gains the click-through to Skool,
         and turns into a real poster on its own as rows are re-mirrored. The overflow
         cap protects nothing here: one message in 820 carries media at all. -->
    {#if images.length || videos.length}
      <div class="mt-1">
        <SkoolMedia {images} {videos} {skoolUrl} />
      </div>
    {/if}

    {#each files as f (f.external_id)}
      <span
        class="mt-1 flex items-center gap-1.5 rounded-lg px-2 py-1 text-[11px]
               {mine ? 'bg-primary-content/15' : 'bg-base-300/60'}"
      >
        <svg class="h-3.5 w-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" /></svg>
        <span class="truncate">{f.file_name ?? 'attachment'}</span>
      </span>
    {/each}

    {#if empty}
      <span class="italic opacity-60">(no content)</span>
    {/if}

    <!-- Relative by default, exact on hover — "3 days ago" is what a reader wants and
         the instant is what a support conversation ends up needing. `exact` puts the
         instant in the line itself; `none` drops the row entirely. See `timestamp`. -->
    {#if timestamp !== 'none'}
    <div
      class="mt-0.5 flex items-center gap-1 {compact ? 'text-[10px]' : 'text-[11px]'} {mine
        ? 'justify-end text-primary-content/70'
        : 'opacity-55'}"
      title={message.createdAt ? fullDate(message.createdAt) : undefined}
    >
      {#if timestamp === 'exact'}
        <span class="font-mono">{message.createdAt ? fullDate(message.createdAt) : '—'}</span>
      {:else}
        {timeAgo(message.createdAt, now)}
      {/if}
      <!-- After the time, on our own side only — the corner a messenger has trained
           every reader to glance at. -->
      {#if mine && receipt}
        <ReadReceipt state={receipt} sentAt={message.createdAt} {now} />
      {/if}
    </div>
    {/if}
  </div>

  {#if said && mine}
    <Avatar
      src={said.pictureUrl}
      name={said.name ?? ''}
      size={compact ? 20 : 26}
      memberId={said.memberId ?? null}
    />
  {/if}
</div>
